import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import { EventHeader, EventTabs, EVENT_TABS } from "../../../../components/Events/Event";
import messages from "../../../../assets/locale/messages";
import * as companyPortalEventsApi from "../../../../network/apis/companyPortalEvents";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  getPortalApiErrorMessage,
  isCompanyPortalEventReadOnly,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import {
  showErrorMsg,
  showSuccessMsg,
} from "../../../../utils/Helpers";
import useEventViewBreadcrumbs from "../../../../hooks/useEventViewBreadcrumbs";
import PaymentNotice from "../../../../components/Events/Event/PaymentNotice";
import {
  EVENT_PAYMENT_STATES,
  getEventPaymentState,
} from "../../../../components/Events/Event/BasicInfo/paymentStateUtils";
import {
  COMPANY_EVENT_DRAFT_TAB,
  getCompanyEventListTab,
} from "../../../../utils/companyPortal/eventListTabs";
import {
  shouldShowEventApplicantsTab,
  shouldShowEventPayrollTab,
} from "../../../../utils/companyPortal/eventEditRules";
import {
  EVENT_REPORT_URL_PARAM,
  isEventPayrollTabEnabled,
  resolveEventViewTabFromSearch,
} from "../../../../utils/eventViewTabs";
import { PAYROLL_URL_PARAMS } from "../../../../components/Events/Event/Payroll/GeneratePayroll/constants/payrollUrlParams";
import { PAYROLL_GENERATE_ALL } from "../../../../components/Events/Event/Payroll/GeneratePayroll/utils/mapPayrollReportData";
import { companyPermissionModules, eventStatuses } from "../../../../utils/Constants";
import {
  canAddCompanyModule,
  canDeleteCompanyModule,
  canEditCompanyModule,
  canViewCompanyModules,
} from "../../../../utils/Permissions";
import { UIActionModal, UIButton } from "../../../../components/ui";
import CompanyViewReport from "../../Reports/ViewReport/ViewReport";

import "./View.scss";

const COMPANY_EVENT_TAB_VALUES = Object.values(EVENT_TABS);

const resolveInitialEventTab = (search, locationState) =>
  resolveEventViewTabFromSearch(search, COMPANY_EVENT_TAB_VALUES) ??
  locationState?.initialTab ??
  EVENT_TABS.basicInfo;

const ViewEvent = () => {
  const { id: routeId } = useParams();
  const location = useLocation();
  const { state: locationState } = location;
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { events, shared } = messages[lang];
  const canEdit = canEditCompanyModule(companyPermissionModules.events, login);
  const canDelete = canDeleteCompanyModule(companyPermissionModules.events, login);
  const decodedId = routeId ? decodeURIComponent(String(routeId)) : "";

  // Kept in a ref so switching language does not re-run the event fetch.
  const generalErrMsgRef = useRef(shared.generalErrMsg);
  generalErrMsgRef.current = shared.generalErrMsg;

  const [event, setEvent] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    resolveInitialEventTab(location.search, locationState),
  );
  const [selectedRound, setSelectedRound] = useState(locationState?.selectedRound ?? null);
  const [actionModal, setActionModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // `activeTab` is local state (not derived from the URL each render); read the
  // generate-payroll param straight from the URL so the header hides while it's open.
  const payrollGenerateUserIdParam =
    new URLSearchParams(location.search).get(PAYROLL_URL_PARAMS.generate) || "";
  const isGeneratingPayroll =
    activeTab === EVENT_TABS.payroll && Boolean(payrollGenerateUserIdParam);
  const isSingleApplicantPayroll =
    isGeneratingPayroll &&
    payrollGenerateUserIdParam !== PAYROLL_GENERATE_ALL &&
    !payrollGenerateUserIdParam.includes(",");
  const payrollGenerateLabel = isGeneratingPayroll
    ? isSingleApplicantPayroll
      ? events.viewEvents.payrollTab.generatePayroll.singleTitle
      : events.viewEvents.payrollTab.generatePayroll.bulkTitle
    : undefined;

  const history = useHistory();
  const canCancel =
    canEdit &&
    event?.status === eventStatuses.upcoming;
  // Published through an accepted publishing request: cancelling refunds it (PROLINE-4175).
  const isPaidEvent =
    getEventPaymentState(event)?.state === EVENT_PAYMENT_STATES.approved;
  // Only a request still waiting on the Super Admin can be cancelled.
  const pendingRequestId =
    event?.status === eventStatuses.pendingPayment
      ? event?.last_publish_payment_request?.id
      : null;
  const canAdd = canAddCompanyModule(companyPermissionModules.events, login);
  const canCancelRequest = (canEdit || canAdd) && Boolean(pendingRequestId);
  // The backend refuses to delete a draft that has any prior payment request
  // (cancelled or rejected), so we hide the button in that case.
  const canDeleteEvent =
    canDelete &&
    event?.status === eventStatuses.draft &&
    !event?.last_publish_payment_request;

  const fetchEvent = useCallback(
    async ({ silent = false } = {}) => {
      if (!decodedId) {
        setEvent(null);
        return;
      }

      try {
        const res = await companyPortalEventsApi.getCompanyPortalEvent(decodedId);
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        setEvent({ id: unwrapped?.id ?? decodedId, ...attrs });
      } catch (e) {
        if (!silent) {
          showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
          setEvent(null);
          const status = e?.response?.status;
          if (status === 404 || status === 403) {
            history.replace(ROUTE_PATHS.companyEvents);
          }
        }
      }
    },
    [decodedId, history],
  );

  useEffect(() => {
    if (!decodedId) {
      setEvent(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await companyPortalEventsApi.getCompanyPortalEvent(decodedId);
        if (cancelled) return;
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        setEvent({ id: unwrapped?.id ?? decodedId, ...attrs });
      } catch (e) {
        if (!cancelled) {
          showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
          setEvent(null);
          const status = e?.response?.status;
          if (status === 404 || status === 403) {
            history.replace(ROUTE_PATHS.companyEvents);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Refetch on a language change: names (crew size, event type, roles) come back in the request locale.
  }, [decodedId, history, lang]);

  // Company event tabs are controlled locally; keep them in sync when deep-linking
  // via `?tab=` (e.g. Reports breadcrumb from report detail).
  useEffect(() => {
    const tabFromUrl = resolveEventViewTabFromSearch(
      location.search,
      COMPANY_EVENT_TAB_VALUES,
    );
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  const showApplicantsTab = useMemo(
    () => shouldShowEventApplicantsTab(event),
    [event],
  );

  const showTimesheetTab = useMemo(
    () =>
      showApplicantsTab &&
      canViewCompanyModules(companyPermissionModules.timesheet, login),
    [login, showApplicantsTab],
  );

  const showReportsTab = useMemo(
    () => canViewCompanyModules(companyPermissionModules.reports, login),
    [login],
  );

  const showClientReportsTab = useMemo(
    () => canViewCompanyModules(companyPermissionModules.manageReports, login),
    [login],
  );

  const showPayrollTab = useMemo(
    () =>
      isEventPayrollTabEnabled &&
      showApplicantsTab &&
      shouldShowEventPayrollTab(event) &&
      canViewCompanyModules(companyPermissionModules.payroll, login),
    [event, login, showApplicantsTab],
  );

  const { breadcrumbList } = useEventViewBreadcrumbs({
    event,
    events,
    eventsListPath: ROUTE_PATHS.companyEvents,
    showApplicantsTab,
    showEvaluationsTab: showApplicantsTab,
    showPayrollTab,
    showTimesheetTab,
    showReportsTab,
    showClientReportsTab,
    timesheetPortal: "company",
    activeTab,
    selectedRound,
    onEvaluationsBack: () => setSelectedRound(null),
    onActiveTabBack: () => {
      setSelectedRound(null);
      setActiveTab(EVENT_TABS.basicInfo);
    },
    nestedDetailLabel: payrollGenerateLabel,
    statusTab: getCompanyEventListTab(event),
  });

  const handleEditEvent = () => {
    if (!decodedId) return;
    history.push(ROUTE_PATHS.companyEditEvent.replace(":id", decodedId));
  };

  const handleConfirmCancel = async () => {
    if (!decodedId || actionLoading) return;

    setActionLoading(true);
    try {
      await companyPortalEventsApi.cancelCompanyPortalEvent(decodedId);
      showSuccessMsg("eventCanceledSuccessfully", false);
      setActionModal(null);
      history.push(
        `${ROUTE_PATHS.companyEvents}?tab=${eventStatuses.cancelled}`,
      );
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
    } finally {
      setActionLoading(false);
    }
  };

  /** Cancels the pending publishing request; the event returns to Draft (PROLINE-3923). */
  const handleConfirmCancelRequest = async () => {
    if (!pendingRequestId || actionLoading) return;

    setActionLoading(true);
    try {
      const response = await companyPortalEventsApi.cancelCompanyPortalPaymentRequest(pendingRequestId);
      // The backend's message, or ours while it sends none.
      const message = response?.data?.message;
      showSuccessMsg(message || "paymentRequestCancelledSuccessfully", Boolean(message));
      setActionModal(null);
      fetchEvent();
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
      setActionModal(null);
      fetchEvent();
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!decodedId || actionLoading) return;

    setActionLoading(true);
    try {
      await companyPortalEventsApi.deleteCompanyPortalEvent(decodedId);
      showSuccessMsg("eventDeletedSuccessfully", false);
      setActionModal(null);
      history.push(
        `${ROUTE_PATHS.companyEvents}?tab=${COMPANY_EVENT_DRAFT_TAB}`,
      );
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
    } finally {
      setActionLoading(false);
    }
  };

  const reportIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get(EVENT_REPORT_URL_PARAM),
    [location.search],
  );

  // Keep report detail on the event view URL (`?tab=reports&report=`).
  if (reportIdFromUrl) {
    return <CompanyViewReport />;
  }

  const headerBeforeEditActions =
    canDeleteEvent || canCancel || canCancelRequest ? (
      <>
        {canCancelRequest ? (
          <UIButton
            type="button"
            color="error"
            size="medium"
            onClick={() => setActionModal("cancelRequest")}
          >
            {events.viewEvents.cancelRequest.action}
          </UIButton>
        ) : null}
        {canDeleteEvent ? (
          <UIButton
            type="button"
            variant="outlined"
            appearance="outline"
            color="inherit"
            size="medium"
            onClick={() => setActionModal("delete")}
          >
            {events.viewEvents.deleteBtn}
          </UIButton>
        ) : null}
        {canCancel ? (
          <UIButton
            type="button"
            variant="outlined"
            appearance="outline"
            color="inherit"
            size="medium"
            onClick={() => setActionModal("cancel")}
          >
            {events.viewEvents.cancelBtn}
          </UIButton>
        ) : null}
      </>
    ) : null;

  return (
    <Box
      className="company-event-view"
      sx={{
        marginTop: "var(--space-32)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-16)",
        paddingBottom: "var(--space-32)",
      }}
    >
      <Breadcrumbs
        list={breadcrumbList}
        className="company-event-view__breadcrumbs"
        BreadCrumbHasBackIcon={false}
      />
      {!isGeneratingPayroll && (
        <EventHeader
          event={event}
          statusKey={getCompanyEventListTab(event)}
          editLabel={events.viewEvents.editBtn}
          onEdit={handleEditEvent}
          editDisabled={!decodedId}
          showEditButton={
            canEdit &&
            Boolean(event) &&
            !isCompanyPortalEventReadOnly(event?.status)
          }
          beforeEditAction={headerBeforeEditActions}
          editSize="medium"
        />
      )}
      <EventTabs
        event={event}
        onRefreshEvent={fetchEvent}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        selectedRound={selectedRound}
        onRoundSelect={setSelectedRound}
      />

      <UIActionModal
        open={actionModal === "cancelRequest"}
        onClose={() => !actionLoading && setActionModal(null)}
        title={events.viewEvents.cancelRequest.title}
        variant="reject"
        maxWidth="xs"
        actions={
          <UIActionModal.Actions
            cancelLabel={events.viewEvents.cancelRequest.keep}
            confirmLabel={events.viewEvents.cancelRequest.action}
            confirmVariant="reject"
            onCancel={() => setActionModal(null)}
            onConfirm={handleConfirmCancelRequest}
            confirmDisabled={actionLoading}
          />
        }
      >
        <Typography className="company-event-view__modal-copy">
          {events.viewEvents.cancelRequest.message}
        </Typography>
        <PaymentNotice
          title={events.viewEvents.cancelRequest.refundTitle}
          description={events.viewEvents.cancelRequest.refundMessage}
        />
      </UIActionModal>

      <UIActionModal
        open={actionModal === "delete" || actionModal === "cancel"}
        onClose={() => !actionLoading && setActionModal(null)}
        title={events.viewEvents.confirmation || shared.confirm}
        variant="reject"
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={shared.confirm}
            confirmVariant="reject"
            onCancel={() => setActionModal(null)}
            onConfirm={
              actionModal === "delete" ? handleConfirmDelete : handleConfirmCancel
            }
            confirmDisabled={actionLoading}
          />
        }
      >
        <Typography className="company-event-view__modal-copy">
          {actionModal === "delete"
            ? events.viewEvents.deleteModalConfirmMessage
            : events.viewEvents.cancelModalConfirmMessage}
        </Typography>
        {actionModal === "cancel" && isPaidEvent ? (
          <PaymentNotice
            title={events.viewEvents.cancelRequest.refundTitle}
            description={events.viewEvents.cancelRequest.refundMessage}
          />
        ) : null}
      </UIActionModal>
    </Box>
  );
};

export default ViewEvent;
